import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { PATHIS, PREACHERS, WEEKDAYS_HI, GHAR_TYPE_HI, withHonorific, splitTitle, applyTitle, defaultHM, fmt12 } from "../data/rssbData";
import { hinglishToHindi } from "../lib/translit";
import { HindiInput } from "./HindiInput";
import { TitlePicker } from "./TitlePicker";
import { PersonPicker } from "./PersonPicker";
import { dutiesOn } from "../lib/clashes";

const toHi = async (v) => ((v || "").trim() ? (await hinglishToHindi(v.trim())).trim() : "");
const titledHi = async (title, v) => applyTitle(title, await toHi(v));

const TYPES = [
  { v: "SR", label: "SR — सत्संग रीडर" },
  { v: "SK", label: "SK — सत्संग कर्ता" },
  { v: "CD", label: "CD — सी डी सत्संग" },
  { v: "LCD", label: "LCD — सवाल-जवाब" },
  { v: "SPECIAL", label: "Special SK — विशेष कर्ता" },
];

const CUSTOM = "__custom__";

// Sewadars from this satsang ghar first, then its center group, then everyone else.
const norm = (s) => (s || "").replace(/[\sािीुूंँ़–-]/g, "").replace(/झ/g, "ज");
const rankFor = (ghar) => (p) => {
  if (!ghar) return 2;
  const a = norm(p.placeHi), b = norm(ghar.hi);
  if (a && (a === b || a.startsWith(b) || b.startsWith(a))) return 0;
  return p.group === ghar.group ? 1 : 2;
};
const sortedFor = (list, ghar) => {
  const r = rankFor(ghar);
  return list.map((p) => ({ p, r: r(p) })).sort((x, y) => x.r - y.r);
};
const toOptions = (list, ghar, withRole, busy = {}, myTime = "") =>
  sortedFor(list, ghar).map(({ p, r }) => {
    const b = busy[`id:${p.id}`];
    return {
      value: p.id,
      rank: r,
      label: `${withHonorific(p)}${withRole ? ` · ${p.role}` : ""} · ${p.placeHi}`,
      search: `${p.en || ""} ${p.hi} ${p.placeHi || ""} ${p.mobile || ""}`,
      warn: b ? (b.some((x) => x.time === myTime) ? "clash" : "busy") : null,
      note: b ? b.map((x) => `${x.ghar} ${x.time}`).join(", ") : "",
    };
  });
const BusyNote = ({ opt, testid }) =>
  opt?.warn ? (
    <p data-testid={testid} className={`mt-1 text-xs font-semibold font-dev ${opt.warn === "clash" ? "text-red-700" : "text-amber-700"}`}>
      {opt.warn === "clash" ? "⚠ इसी समय पहले से सेवा है: " : "इस तारीख को पहले से सेवा है: "}
      {opt.note}
    </p>
  ) : null;
const EXTRA = [
  { value: "none", label: "— कोई नहीं —", search: "none koi nahi" },
  { value: CUSTOM, label: "✎ अन्य नाम लिखें…", search: "anya naya new other custom" },
];

export const CellEditor = ({ open, onClose, ghar, date, cell, onSave, onRemove, dayHM, variant }) => {
  const [type, setType] = useState("SR");
  const [preacherId, setPreacherId] = useState("none");
  const [pathiId, setPathiId] = useState("none");
  const [customPreacher, setCustomPreacher] = useState("");
  const [customPathi, setCustomPathi] = useState("");
  const [preacherCity, setPreacherCity] = useState("");
  const [preacherMobile, setPreacherMobile] = useState("");
  const [pathiCity, setPathiCity] = useState("");
  const [pathiMobile, setPathiMobile] = useState("");
  const [specialName, setSpecialName] = useState("");
  const [fromCity, setFromCity] = useState("");
  const [specialMobile, setSpecialMobile] = useState("");
  const [preTitle, setPreTitle] = useState("श्री");
  const [pathiTitle, setPathiTitle] = useState("श्री");
  const [spTitle, setSpTitle] = useState("श्री");
  const [time, setTime] = useState("");

  useEffect(() => {
    if (open) {
      setType(cell?.type || "SR");
      setPreacherId(cell?.customPreacher ? CUSTOM : cell?.preacherId || "none");
      setPathiId(cell?.customPathi ? CUSTOM : cell?.pathiId || "none");
      const cp = splitTitle(cell?.customPreacher);
      const cpa = splitTitle(cell?.customPathi);
      const cs = splitTitle(cell?.special?.name);
      setCustomPreacher(cp.rest);
      setCustomPathi(cpa.rest);
      setPreTitle(cp.title || "श्री");
      setPathiTitle(cpa.title || "श्री");
      setSpTitle(cs.title || "श्री");
      setPreacherCity(cell?.preacherCity || "");
      setPreacherMobile(cell?.preacherMobile || "");
      setPathiCity(cell?.pathiCity || "");
      setPathiMobile(cell?.pathiMobile || "");
      setSpecialName(cs.rest);
      setFromCity(cell?.special?.fromCity || "");
      setSpecialMobile(cell?.special?.mobile || "");
      setTime(cell?.time || (date?.iso ? (cell?.special?.slot !== "noon" && dayHM) || defaultHM(date.iso, cell?.special?.slot) : ""));
    }
  }, [open, cell, date, dayHM]);

  if (!ghar || !date) return null;
  const needsPerson = type === "SK" || type === "SR";
  const isSpecial = type === "SPECIAL";
  const needsPathi = needsPerson || isSpecial;

  const seasonHM = defaultHM(date.iso, cell?.special?.slot);
  const baseHM = (cell?.special?.slot !== "noon" && dayHM) || seasonHM;
  const busy = Object.fromEntries(
    Object.entries(dutiesOn(variant, date.iso))
      .map(([k, list]) => [k, list.filter((x) => x.gid !== ghar.id)])
      .filter(([, list]) => list.length)
  );
  const preOpts = toOptions(PREACHERS, ghar, true, busy, fmt12(time));
  const paOpts = toOptions(PATHIS, ghar, false, busy, fmt12(time));
  const handleSave = async () => {
    const next = {
      time: time && time !== seasonHM ? time : null,
      type,
      preacherId: needsPerson && preacherId !== "none" && preacherId !== CUSTOM ? preacherId : null,
      customPreacher: needsPerson && preacherId === CUSTOM && customPreacher.trim() ? await titledHi(preTitle, customPreacher) : null,
      preacherCity: needsPerson && preacherCity.trim() ? await toHi(preacherCity) : null,
      preacherMobile: needsPerson && preacherMobile.trim() ? preacherMobile.trim() : null,
      pathiId: needsPathi && pathiId !== "none" && pathiId !== CUSTOM ? pathiId : null,
      customPathi: needsPathi && pathiId === CUSTOM && customPathi.trim() ? await titledHi(pathiTitle, customPathi) : null,
      pathiCity: needsPathi && pathiCity.trim() ? await toHi(pathiCity) : null,
      pathiMobile: needsPathi && pathiMobile.trim() ? pathiMobile.trim() : null,
      special: isSpecial
        ? { ...cell?.special, name: await titledHi(spTitle, specialName), fromCity: await toHi(fromCity), mobile: specialMobile.trim() }
        : cell?.special,
      locked: cell?.locked,
    };
    onSave(next);
    onClose();
  };

  const inputCls = "mt-1 bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212] font-dev";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fffdf7] border-[#6e1b1b]/40 text-[#3a1212] max-h-[90vh] overflow-y-auto" data-testid="cell-editor">
        <DialogHeader>
          <DialogTitle className="font-serif-dev text-[#3a1212]">
            {ghar.hi}<span className="text-[#6e1b1b] text-sm">–{GHAR_TYPE_HI[ghar.type]}</span>
          </DialogTitle>
          <p className="text-xs text-[#7a4a3a]">
            {date.day} — {WEEKDAYS_HI[date.dow]}
            {cell?.locked && " · 🔒 विशेष (लॉक)"}
          </p>
          <DialogDescription className="sr-only">सत्संग सेल संपादित करें</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[#5a2a24] text-xs">सत्संग समय / Time</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="time"
                data-testid="editor-time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-36 bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212]"
              />
              <span className="text-sm font-semibold text-[#6e1b1b]" data-testid="editor-time-preview">{fmt12(time)}</span>
              {time !== baseHM && (
                <button type="button" data-testid="editor-time-reset" onClick={() => setTime(baseHM)} className="text-xs underline text-[#7a4a3a]">
                  {dayHM && baseHM === dayHM ? "इस दिन का समय" : "सामान्य समय"} ({fmt12(baseHM)})
                </button>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[#5a2a24] text-xs">सत्संग प्रकार / Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="editor-type" className="mt-1 bg-[#f6eeda] border-[#e3d6b6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSpecial && (
            <div className="space-y-2 rounded-lg border border-[#6e1b1b]/25 p-3">
              <Label className="text-[#5a2a24] text-xs">विशेष कर्ता नाम (हिंग्लिश लिखें — नीचे से हिंदी चुनें)</Label>
              <TitlePicker testid="editor-special-title" value={spTitle} onChange={setSpTitle} />
              <HindiInput
                testid="editor-special-name"
                value={specialName}
                onChange={setSpecialName}
                placeholder="e.g. Rahul Verma"
                className={inputCls}
              />
              <div className="grid grid-cols-2 gap-3">
                <HindiInput
                  testid="editor-special-city"
                  value={fromCity}
                  onChange={setFromCity}
                  placeholder="स्थान/शहर (वैकल्पिक)"
                  className={inputCls}
                />
                <Input
                  data-testid="editor-special-mobile"
                  value={specialMobile}
                  onChange={(e) => setSpecialMobile(e.target.value)}
                  placeholder="मोबाइल (वैकल्पिक)"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          {needsPerson && (
            <div className="space-y-2 rounded-lg border border-[#e3d6b6] p-3">
              <Label className="text-[#5a2a24] text-xs">प्रचारक (SR/SK)</Label>
              <PersonPicker testid="editor-preacher" value={preacherId} onChange={setPreacherId} extra={EXTRA} options={preOpts} />
              <BusyNote testid="editor-preacher-busy" opt={preOpts.find((o) => o.value === preacherId)} />
              {preacherId === CUSTOM && (
                <div className="space-y-2">
                  <TitlePicker testid="editor-preacher-title" value={preTitle} onChange={setPreTitle} />
                  <HindiInput
                    testid="editor-custom-preacher"
                    value={customPreacher}
                    onChange={setCustomPreacher}
                    placeholder="नया नाम — हिंग्लिश लिखें, नीचे से हिंदी चुनें"
                    className="mt-1 bg-[#f6eeda] border-[#6e1b1b]/40 text-[#3a1212] font-dev"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <HindiInput
                  testid="editor-preacher-city"
                  value={preacherCity}
                  onChange={setPreacherCity}
                  placeholder="स्थान/लोकैलिटी (वैकल्पिक)"
                  className={inputCls}
                />
                <Input
                  data-testid="editor-preacher-mobile"
                  value={preacherMobile}
                  onChange={(e) => setPreacherMobile(e.target.value)}
                  placeholder="मोबाइल (वैकल्पिक)"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          {needsPathi && (
            <div className="space-y-2 rounded-lg border border-[#e3d6b6] p-3">
              <Label className="text-[#5a2a24] text-xs">पाठी / Pathi</Label>
              <PersonPicker testid="editor-pathi" value={pathiId} onChange={setPathiId} extra={EXTRA} options={paOpts} />
              <BusyNote testid="editor-pathi-busy" opt={paOpts.find((o) => o.value === pathiId)} />
              {pathiId === CUSTOM && (
                <div className="space-y-2">
                  <TitlePicker testid="editor-pathi-title" value={pathiTitle} onChange={setPathiTitle} />
                  <HindiInput
                    testid="editor-custom-pathi"
                    value={customPathi}
                    onChange={setCustomPathi}
                    placeholder="नया पाठी नाम — हिंग्लिश लिखें, नीचे से हिंदी चुनें"
                    className="mt-1 bg-[#f6eeda] border-[#6e1b1b]/40 text-[#3a1212] font-dev"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <HindiInput
                  testid="editor-pathi-city"
                  value={pathiCity}
                  onChange={setPathiCity}
                  placeholder="स्थान/लोकैलिटी (वैकल्पिक)"
                  className={inputCls}
                />
                <Input
                  data-testid="editor-pathi-mobile"
                  value={pathiMobile}
                  onChange={(e) => setPathiMobile(e.target.value)}
                  placeholder="मोबाइल (वैकल्पिक)"
                  className={inputCls}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          {cell ? (
            <Button
              variant="destructive"
              data-testid="editor-remove"
              onClick={() => {
                onRemove();
                onClose();
              }}
              className="gap-1"
            >
              सेल हटाएं
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={onClose} className="text-[#5a2a24]">रद्द करें</Button>
            <Button
              data-testid="editor-save"
              onClick={handleSave}
              className="bg-gradient-to-b from-[#7a1f1f] to-[#5e1414] border border-[#431010] text-[#f3ead6] font-semibold btn-3d-red"
            >
              सहेजें
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
